'use client';

import ExplainDialog from '@/app/_lens-components/components/ExplainDialog';
import LensIndexPrice from '@/app/_lens-components/components/LensIndexPrice';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { resolveLensTypeTrackingFields } from '@/app/_lens-components/tracking';
import { LensIndexItem } from '@/app/_lens-components/types';
import {
  getApiPrescriptionType,
  handleColorItemPriceInfo,
  handleLensIndexPriceInfo,
  resolvePromotionDisplayInfo,
} from '@/app/_lens-components/utils';
import { useDefaultSelection } from '@/app/_lens-components/DefaultSelectionContext';
import { mergePrevLensIndexSelection } from '@/app/_lens-components/utils/operatePrevLensSelection';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import Image from '@/components/Image';
import Tooltip from '@/components/Tooltip';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { processLineBreaks } from '@/utils/tools';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useWhenCurUserInfoReady } from '@/hooks/useWhenReady';
import { useOpticalProcedureTitle } from '@/app/_lens-components/copywriting/useOpticalProcedureTitle';

export default function LensIndex({
  lensIndexTip,
  selectedLensIndexItem,
  onSelectedLensIndexItem,
}: {
  lensIndexTip: string;
  selectedLensIndexItem: LensIndexItem | null;
  onSelectedLensIndexItem: (lensIndexItem: LensIndexItem) => void;
}) {
  const { canModifyPrevSelection } = useDefaultSelection();
  const t = useTranslations('lens-process');
  const lensTitle = useOpticalProcedureTitle('lens_index', t('lens'));
  const recommendedTitle = useOpticalProcedureTitle('recommended', t('recommendedForYou'));
  const {
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
    selectedLensType,
    selectedBrand,
    selectedColorItem,
    hasIndexPrice,
    lensTypeForTracking,
  } = useLensProcessStore();
  const { trackEvent } = useReporting();
  const lensIndexList = selectedColorItem?.lens_list || [];
  const [showExplainPop, setShowExplainPop] = useState(false);
  const lensIndexGroupLabelId = useId();
  const lensIndexRadioName = useId();
  const [explainContent, setExplainContent] = useState<{
    index_name?: string;
    index_explain?: string;
  }>({});
  const userInfo = useStore((state) => state.userInfo);
  const currency = useStore((state) => state.currency);
  const waiMode = useStore((state) => state.waiMode);
  const isWap = useMediaQuery('(max-width:1280px)');
  const lensTypeTrackingFields = resolveLensTypeTrackingFields({
    lensType: selectedLensType,
    brand: selectedBrand,
    lensTypeForTracking,
  });
  const selectedLensIndexRowRef = useRef<HTMLLIElement | null>(null);

  useLayoutEffect(() => {
    if (!selectedLensIndexItem?.id) return;
    selectedLensIndexRowRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
      inline: 'nearest',
    });
  }, [selectedLensIndexItem?.id, selectedColorItem?.lens_color]);

  useWhenCurUserInfoReady(() => {
    // 埋点：页面浏览
    try {
      const colorItemPriceInfo = handleColorItemPriceInfo(
        selectedColorItem,
        false,
        currency,
        userInfo,
      );
      trackEvent(
        {
          eventName: 'LensIndexPage_View',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            ...lensTypeTrackingFields,
            lens_color: selectedColorItem?.color_name,
            lens_price: parseFloat(colorItemPriceInfo.price),
            current_lens_price: parseFloat(colorItemPriceInfo.sell_price),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }, []);

  // 处理描述信息
  const handleDes = (item: LensIndexItem) => {
    const { brand_description = '' } = item;
    const brandArr = brand_description
      ? brand_description.replace(/\\n|\n/g, '/n@#@').split('/n@#@')
      : [];

    return brandArr.map((text: string) => ({
      text,
      premium: 1,
    }));
  };

  // 是否显示高级标签
  const showPremium = (item: LensIndexItem) => {
    return item.index_brand && item.index_brand === 'pro';
  };

  // 显示说明弹窗
  const showExplain = (item: LensIndexItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setExplainContent(item);
    setShowExplainPop(true);
  };

  // 选择折射率
  const onItemClick = (item: LensIndexItem) => {
    // 如果选中的和已选中的一样，不做操作
    if (selectedLensIndexItem && item.id === selectedLensIndexItem.id) {
      return;
    }

    onSelectedLensIndexItem(item);
    if (canModifyPrevSelection) {
      mergePrevLensIndexSelection(item);
    }

    // 这里可以添加埋点逻辑
    SDLensIndexPageSelectClick(item);
  };

  function SDLensIndexPageSelectClick(lensIndexItem: LensIndexItem) {
    try {
      const lensIndexPriceInfo = handleLensIndexPriceInfo(lensIndexItem, currency, userInfo);
      const colorItemPriceInfo = handleColorItemPriceInfo(
        selectedColorItem,
        hasIndexPrice,
        currency,
        userInfo,
      );
      trackEvent(
        {
          eventName: 'LensIndexPage_Select_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            lens_id: `${lensIndexItem.id}`,
            lens_price: parseFloat(colorItemPriceInfo.price),
            current_lens_price: parseFloat(colorItemPriceInfo.sell_price),
            lens_index_name: lensIndexItem.index_name,
            lens_index: lensIndexItem.refractive_index,
            lens_code: lensIndexItem.code,
            lens_index_price: parseFloat(lensIndexPriceInfo.price),
            current_lens_index_price: parseFloat(lensIndexPriceInfo.sell_price),
            currency: currency.name,

            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            ...lensTypeTrackingFields,
            lens_color: selectedColorItem?.color_name,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  return (
    <div className="font-br h-full box-border overflow-y-hidden">
      <div className="overflow-auto h-full">
        <h3 className={stepCommonClassName.stepTitle}>
          <span>{lensTitle}</span>
        </h3>

        {lensIndexTip && (
          <p className="max-xl:mx-[14px] mb-[9px] xl:mb-[13px] p-[6px_10px] xl:p-[12px_14px] leading-[16px] text-[12px] xl:text-[14px] font-br bg-[#fff9d7] rounded-[6px]">
            {lensIndexTip}
          </p>
        )}

        {Array.isArray(lensIndexList) && lensIndexList.length > 0 ? (
          <div role="radiogroup" aria-labelledby={lensIndexGroupLabelId}>
            <span id={lensIndexGroupLabelId} className="sr-only">
              {t('lens')}
            </span>
            <ul className={stepCommonClassName.optionsContainer}>
              {lensIndexList.map((item: any, index: number) => {
                const isLensIndexSelected =
                  !!selectedLensIndexItem && selectedLensIndexItem.id === item.id;
                const optionId = `${lensIndexRadioName}-${item.id ?? index}`;
                const optionTitleId = `${optionId}-title`;
                const optionDescriptionId = `${optionId}-description`;
                const recommendBadgeId = `${optionId}-recommend`;
                const describedByIds = [
                  optionDescriptionId,
                  item.recommend === 1 ? recommendBadgeId : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <li
                    key={index}
                    ref={(el) => {
                      if (isLensIndexSelected && selectedLensIndexRowRef.current !== el) {
                        selectedLensIndexRowRef.current = el;
                      }
                    }}
                    className={`${stepCommonClassName.optionItem} ${
                      item.recommend === 1 ? stepCommonClassName.recommendedItem : ''
                    }`}
                  >
                    {item.recommend === 1 && (
                      <div id={recommendBadgeId} className={stepCommonClassName.recommendBadge}>
                        <div className={stepCommonClassName.recommendBadgeContent}>
                          <span>{recommendedTitle}</span>
                        </div>
                      </div>
                    )}

                    <div
                      className={`${stepCommonClassName.optionBox} ${
                        selectedLensIndexItem && selectedLensIndexItem.id === item.id
                          ? stepCommonClassName.selectedOptionBox
                          : ''
                      }`}
                    >
                      <input
                        id={optionId}
                        className="peer absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer opacity-0"
                        type="radio"
                        name={lensIndexRadioName}
                        checked={isLensIndexSelected}
                        onChange={() => onItemClick(item)}
                        aria-labelledby={optionTitleId}
                        aria-describedby={describedByIds}
                      />
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 z-[1] rounded-[6px] peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-primary-purple"
                      />
                      <div
                        className={`${stepCommonClassName.optionContent} relative z-[2] pointer-events-none`}
                      >
                        <div className={`${stepCommonClassName.imageContainer} min-w-[60px]`}>
                          <Image
                            className="w-[60px] h-[60px]"
                            src={item.index_img || ''}
                            alt={item.index_name || ''}
                            width={60}
                            height={60}
                          />
                        </div>

                        <div className={`${stepCommonClassName.textContainer} mt-[7px]`}>
                          <div className={stepCommonClassName.textCenterContainer}>
                            <div className={stepCommonClassName.titleContainer}>
                              <p id={optionTitleId}>{item.index_name}</p>
                              {showPremium(item) && (
                                <div className="ml-[3px] xl:ml-[6px] p-[0_9px] h-[13px] xl:h-[16px] bg-[#f2ddbc] rounded-[8px] font-br text-[#3a2107] text-[12px] flex items-center justify-center">
                                  <span className="relative top-[1px]">{t('premium')}</span>
                                </div>
                              )}
                              {item.index_explain && (
                                <Tooltip
                                  title={
                                    <p
                                      className="text-[14px]"
                                      dangerouslySetInnerHTML={{
                                        __html: processLineBreaks(item.index_explain),
                                      }}
                                    />
                                  }
                                  placement="bottom"
                                  variant="light"
                                  sx={{
                                    zIndex: 1500,
                                    '& .MuiTooltip-tooltip': {
                                      fontSize: '14px',
                                      maxWidth: '490px',
                                    },
                                  }}
                                >
                                  <QuestionResponsive
                                    className={`${stepCommonClassName.questionIcon} pointer-events-auto`}
                                    ariaLabel={item.index_name || t('lens')}
                                    onClick={(e) => {
                                      isWap && showExplain(item, e);
                                      e.stopPropagation();
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </div>

                            <div
                              id={optionDescriptionId}
                              className={`${stepCommonClassName.description(waiMode)}`}
                            >
                              {handleDes(item).map(
                                (desItem: { text: string; premium: number }, desIndex: number) => (
                                  <p
                                    key={desIndex}
                                    className="break-all mb-[10px] leading-[1] flex items-center justify-start mt-[16px]"
                                  >
                                    <span
                                      className={`inline-block w-[4px] h-[4px] ${waiMode ? 'bg-[#282828]' : 'bg-[grey]'} rounded-[50%] mr-[6px]`}
                                    />
                                    <span className="text-primary-black">{desItem.text}</span>
                                  </p>
                                ),
                              )}
                            </div>
                          </div>

                          <div className={`${stepCommonClassName.priceContainer}`}>
                            <LensIndexPrice
                              renderPrice={(price, sellPrice) => (
                                <span className={stepCommonClassName.priceInnerBox}>
                                  {Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                                    <span className={stepCommonClassName.priceText}>
                                      {sellPrice}
                                    </span>
                                  ) : (
                                    <span className={stepCommonClassName.priceText}>
                                      {t('free')}
                                    </span>
                                  )}
                                  {price !== sellPrice && (
                                    <span
                                      className={`${stepCommonClassName.originalPriceText(waiMode)}`}
                                    >
                                      {price}
                                    </span>
                                  )}
                                </span>
                              )}
                              currency={currency}
                              currencyName={currency.name}
                              lensIndexItem={item}
                            />

                            {resolvePromotionDisplayInfo(item).promotionDisplayLabel && (
                              <div
                                className={`${stepCommonClassName.discountBadge(
                                  resolvePromotionDisplayInfo(item).applyMembershipLevel,
                                )} p-[0px_6px] flex items-center justify-center`}
                              >
                                <span className="relative top-[1px]">
                                  {resolvePromotionDisplayInfo(item).promotionDisplayLabel}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className={stepCommonClassName.emptyState}>
            <Image
              src="/static-page/null.png"
              alt={t('noLensesAvailable')}
              width={118}
              height={118}
            />
            <p className={stepCommonClassName.emptyStateText}>
              {t('sorryNoLensesAvailableForYourPrescription')}
            </p>
            <button className={stepCommonClassName.emptyStateButton}>
              {t('editPrescription')}
            </button>
          </div>
        )}
      </div>

      {/* 镜片说明弹窗 */}
      <ExplainDialog
        open={showExplainPop}
        ariaLabel={explainContent?.index_name || t('lens')}
        onClose={() => setShowExplainPop(false)}
      >
        <div>
          <h3 className="text-primary-black text-center text-[16px] mb-[16px] font-normal font-bs">
            {explainContent?.index_name}
          </h3>
          <p
            className="text-[13px] leading-[24px] font-br"
            dangerouslySetInnerHTML={{ __html: processLineBreaks(explainContent?.index_explain) }}
          />
        </div>
      </ExplainDialog>
    </div>
  );
}
